// BookTickets/stages/PaymentProcess/components/MbWayForm/hooks/useMbWayPayment.ts
'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'

import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import type { Cart } from '@booking/types/booking'

import { payTransaction, getTransactionStatus, finalizeTransaction } from '@booking/services/api'

import trackGA4 from '@booking/services/trackingGa4'
import { transformLinesToItems } from '@booking/services/trackingGa4'

import {
  breadcrumb,
  captureBookingMessage,
  captureBookingException,
  setBookingContext,
} from '@booking/services/sentryBooking'

import {
  buildMbWaySuccessPath,
  buildMbWaySuccessRoute,
  MBWAY_POLL_INTERVAL_MS,
  MBWAY_POLL_MAX_ATTEMPTS,
  normalizeMbWayPhone,
  shouldRebuildMbWayCart,
} from '../lib/mbWayPayment'

import {
  pollTransactionStatus,
  type PollTransactionStatusResult,
} from '../lib/pollTransactionsStatus'

type UseMbWayPaymentArgs = {
  //rebuildCart: () => Promise<Cart>
  hasFired: boolean
  onFire?: () => Promise<void> | void
}

type UseMbWayPaymentReturn = {
  waiting: boolean
  error: string | null
  start: (phone: string) => Promise<void>
}

export function useMbWayPayment({
  //rebuildCart,
  hasFired,
  onFire,
}: UseMbWayPaymentArgs): UseMbWayPaymentReturn {
  const t = useTranslations('booking')
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const params = useParams<{ slug?: string }>()
  const slug = params?.slug ?? ''
  const flow = searchParams.get('flow') ?? undefined

  const state = useBookingState()
  const { dispatch, rebuildCart } = useBookingActionsCtx()

  const cart = state.cart
  const session = state.session

  const [waiting, setWaiting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // lets us cancel polling if component unmounts
  const cancelRef = useRef<null | (() => void)>(null)

  useEffect(() => {
    return () => {
      cancelRef.current?.()
      cancelRef.current = null
    }
  }, [])

  const finishWithError = useCallback((message: string) => {
    setWaiting(false)
    setError(message)
  }, [])

  const trackAddPaymentInfo = useCallback(
    (activeCart: Cart) => {
      trackGA4.addPaymentInfo?.(
        'mbway',
        transformLinesToItems({ cart: activeCart, session, quantities: state.quantities }),
      )
    },
    [session, state.quantities],
  )

  const pollFinalStatus = useCallback(
    async (cartId: string): Promise<PollTransactionStatusResult> => {
      const { cancel, promise } = pollTransactionStatus({
        cartId,
        intervalMs: MBWAY_POLL_INTERVAL_MS,
        maxAttempts: MBWAY_POLL_MAX_ATTEMPTS,
        getStatus: async () => {
          const tx = await getTransactionStatus(cartId)
          return tx?.status as string | undefined
        },
        onTick: (status, attempt) => {
          breadcrumb('mbway.poll.tick', { cartId, status, attempt }, 'booking.payment')
        },
      })

      cancelRef.current = cancel
      const finalStatus = await promise
      cancelRef.current = null
      return finalStatus
    },
    [],
  )

  const start = useCallback(
    async (phoneRaw: string) => {
      const phone = normalizeMbWayPhone(phoneRaw)
      console.log('starting')
      console.log(cart)

      if (!cart) {
        setError(t('ticket.payment.error'))
        return
      }

      if (!phone) return

      setError(null)
      setWaiting(true)

      breadcrumb('mbway.start', { cartId: cart.id, hasFired }, 'booking.payment')

      try {
        // ensure usable cart
        let activeCart = cart
        if (shouldRebuildMbWayCart(cart, hasFired)) {
          breadcrumb('mbway.cart.rebuild', { cartId: cart.id }, 'booking.payment')

          try {
            const rebuiltCart = await rebuildCart()
            if (rebuiltCart) activeCart = rebuiltCart
          } catch (e) {
            console.log(e)
          }
        }

        setBookingContext({ payment_method: 'mbway', cart_id: activeCart.id, route: pathname })
        if (activeCart.type === 'standard') trackAddPaymentInfo(activeCart)

        const successPath = buildMbWaySuccessPath(activeCart.id, flow)

        await payTransaction(activeCart.id, 'REDUNIQ_MB_WAY', {
          successPath,
          customerPhone: phone,
        })
        await onFire?.()

        const finalStatus = await pollFinalStatus(activeCart.id)

        if (finalStatus === 'SOLD') {
          finalizeTransaction(activeCart.id)

          dispatch({ type: 'SET_HAS_COMPLETED', hasCompleted: true })
          setWaiting(false)

          if (activeCart.type === 'standard') {
            trackGA4.purchase(
              activeCart,
              transformLinesToItems({
                cart: activeCart,
                quantities: state.quantities,
                session: state.session,
              }),
            )
          }

          router.push(buildMbWaySuccessRoute(slug, activeCart.id, flow))
          return
        }

        if (finalStatus === 'OPEN') {
          finishWithError(t('ticket.payment.error'))
          captureBookingMessage('MBWAY_STATUS_OPEN', { cartId: activeCart.id }, 'info')
          return
        }

        // timeout (or unknown)
        finishWithError(t('ticket.errors.cartTimeout'))
        captureBookingMessage('MBWAY_POLL_TIMEOUT', { cartId: activeCart.id }, 'warning')
        // tags: { payment_method: 'mbway' },
      } catch (e) {
        captureBookingException(e, 'MBWAY_EXCEPTION', {
          cartId: cart.id,
          payment_method: 'mbway',
          area: 'start',
        })
        setWaiting(false)
        setError(t('ticket.payment.error'))
      }
    },
    [
      cart,
      dispatch,
      finishWithError,
      flow,
      hasFired,
      onFire,
      pathname,
      pollFinalStatus,
      rebuildCart,
      router,
      slug,
      t,
      state.session,
      state.quantities,
      trackAddPaymentInfo,
    ],
  )

  return { waiting, error, start }
}
